import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import socket from "../../../../config/socket";
import { fetchEmployees } from "../../../../store/slices/employeeSlice";
import { fetchGroups } from "../../../../store/slices/groupSlice";
import { fetchAllProjects } from "../../../../store/slices/projectsSlice";
import {
  addMessage,
  addPendingMessage,
  applyUnreadSummary,
  clearTyping,
  createChat,
  deleteChat,
  deleteChatMessage,
  fetchChats,
  fetchUnreadSummary,
  fetchMessages,
  lockChat,
  markPendingAsFailed,
  markChatAsRead,
  postMessage,
  setActiveChat,
  setChatError,
  setChatLock,
  setParticipantIDs,
  setTyping,
  unlockChat,
  uploadChatFile,
} from "../../../../store/slices/chatSlice";
import ChatLeftPanel from "../ui/ChatLeftPanel";
import ChatTopBar from "../ui/ChatTopBar";
import ChatWindow from "../ui/ChatWindow";

const PAGE_LIMIT = 20;

const getChatId = (chat = {}) =>
  String(chat?._id || chat?.id || chat?.chatId || chat?.chat?._id || chat?.chat?.id || "");
const getFullName = (user = {}) =>
  `${user?.EmpFirstName || ""} ${user?.EmpLastName || ""}`.trim() || user?.name || "Unknown";
const isGroupChat = (chat = {}) => {
  const type = String(chat?.type || "").toLowerCase();
  if (type === "group") return true;
  if (type === "direct") return false;
  const members = Array.isArray(chat?.members)
    ? chat.members
    : Array.isArray(chat?.participants)
      ? chat.participants
      : [];
  return members.length > 2;
};
const resolveChatDisplayName = (chat = {}, loggedUserId, employees = []) => {
  const members = Array.isArray(chat?.participants)
    ? chat.participants
    : Array.isArray(chat?.members)
      ? chat.members
      : [];

  if (isGroupChat(chat)) {
    return chat?.name || chat?.displayName || chat?.groupName || "Unnamed Group";
  }

  const loggedId = String(loggedUserId || "");
  const other = members.find((member) => {
    const id = String(member?._id || member?.id || member || "");
    return id && id !== loggedId;
  });

  if (other && typeof other === "object") {
    const name = `${other?.EmpFirstName || ""} ${other?.EmpLastName || ""}`.trim() || other?.name;
    if (name) return name;
  }

  const otherId = String(other?._id || other?.id || other || "");
  if (otherId) {
    const matched = employees.find((emp) => String(emp?._id || "") === otherId);
    if (matched) return getFullName(matched);
  }

  return chat?.displayName || chat?.name || "Direct chat";
};
const getCreatorId = (chat = {}) =>
  String(
    chat?.createdBy?._id ||
      chat?.createdBy ||
      chat?.creatorId ||
      chat?.createdById ||
      chat?.ownerId ||
      chat?.adminId ||
      ""
  );
const normalizeValue = (value) => String(value ?? "").trim();
const normalizeComparable = (value) => normalizeValue(value).toLowerCase();
const getFirstNonEmpty = (...values) => values.map(normalizeValue).find(Boolean) || "";
const getRefId = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    return normalizeValue(value?._id || value?.id || value?.value);
  }
  return normalizeValue(value);
};
const getGroupNames = (value) => {
  if (!value || typeof value !== "object") return [];
  return [
    value?.newGroupName,
    value?.groupName,
    value?.groupname,
    value?.name,
    value?.title,
  ]
    .map(normalizeComparable)
    .filter(Boolean);
};
const getProjectGroupIds = (project = {}) =>
  [
    project?.groupid,
    project?.groupId,
    project?.group,
    project?.groupID,
    project?.GroupId,
    project?.AssignedGroupId,
    project?.lead?.groupid,
    project?.lead?.groupId,
  ]
    .map(getRefId)
    .filter(Boolean);
const getProjectGroupNames = (project = {}) =>
  [
    project?.AssignedGroup,
    project?.GroupName,
    project?.Group,
    project?.groupName,
    project?.groupname,
    project?.group?.newGroupName,
    project?.group?.groupName,
    project?.group?.GroupName,
    project?.group?.name,
    project?.groupid?.newGroupName,
    project?.groupid?.groupName,
    project?.groupid?.GroupName,
    project?.groupid?.name,
    project?.groupId?.newGroupName,
    project?.groupId?.groupName,
    project?.groupId?.GroupName,
    project?.groupId?.name,
    project?.lead?.AssignedGroup,
    project?.lead?.GroupName,
  ]
    .map(normalizeComparable)
    .filter(Boolean);
const getProjectName = (project = {}) =>
  getFirstNonEmpty(project?.ProjectName, project?.projectName, project?.projectname, project?.name);
const getProjectQuotationId = (project = {}) =>
  getFirstNonEmpty(project?.quotationId, project?.QuotationId, project?.quotationNumber, project?.leadid);
const getProjectOptionValue = (project = {}) =>
  getProjectQuotationId(project) || getFirstNonEmpty(project?._id, project?.id) || getProjectName(project);
const unwrapUploadResponse = (uploaded) => {
  if (Array.isArray(uploaded)) return uploaded[0] || {};
  return uploaded?.file || uploaded?.attachment || uploaded?.uploadedFile || uploaded?.data || uploaded || {};
};
const normalizeUploadedAttachment = (uploaded, fallbackFile = {}) => {
  const fileData = unwrapUploadResponse(uploaded);
  const fileId = getFirstNonEmpty(
    fileData?.fileId,
    fileData?.attachmentId,
    fileData?._id,
    fileData?.id,
    uploaded?.fileId,
    uploaded?._id,
    uploaded?.id
  );
  const fileName = getFirstNonEmpty(
    fileData?.name,
    fileData?.fileName,
    fileData?.filename,
    fileData?.originalname,
    uploaded?.name,
    uploaded?.fileName,
    uploaded?.filename,
    fallbackFile?.name
  );
  const fileUrl = getFirstNonEmpty(
    fileData?.url,
    fileData?.fileUrl,
    fileData?.filepathurl,
    fileData?.path,
    fileData?.location,
    uploaded?.url,
    uploaded?.fileUrl,
    uploaded?.filepathurl
  );

  return {
    id: fileId,
    fileId,
    attachmentId: fileId,
    name: fileName,
    fileName,
    filename: fileName,
    url: fileUrl,
    fileUrl,
    filepathurl: fileUrl,
    mimetype: fileData?.mimetype || fileData?.mimeType || fallbackFile?.type || "",
    size: fileData?.size || fallbackFile?.size || 0,
  };
};
const buildChatNameFromMembers = (memberNames = []) => {
  const cleaned = memberNames.map((item) => String(item || "").trim()).filter(Boolean);
  if (cleaned.length === 0) return "Direct chat";
  if (cleaned.length === 1) return cleaned[0];
  if (cleaned.length === 2) return `${cleaned[0]} and ${cleaned[1]}`;
  return `${cleaned[0]} and +${cleaned.length - 1} others`;
};

const ChatFilters = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const userRole = localStorage.getItem("auth");
  const loggedUser = useSelector((state) => state.employees.loggedUser);
  const { employees } = useSelector((state) => state.employees);
  const groupsState = useSelector((state) => state.group.group || []);
  const projectsState = useSelector((state) => state.projects.projects || []);
  const {
    chats,
    activeChatId,
    messagesByChatId,
    paginationByChatId,
    lockByChatId,
    typingByChatId,
    loadingMessages,
    creatingChat,
  } = useSelector((state) => state.chat);

  const [input, setInput] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [selectedCreateUsers, setSelectedCreateUsers] = useState([]);
  const [chatMode, setChatMode] = useState("direct");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedQuotationId, setSelectedQuotationId] = useState("");
  const typingDebounceRef = useRef(null);
  const typingClearRef = useRef(null);
  const messagesRef = useRef(null);
  const chatsRef = useRef([]);
  const loggedUserIdRef = useRef("");

  const activeChat = useMemo(
    () => (chats || []).find((chat) => getChatId(chat) === String(activeChatId || "")) || null,
    [chats, activeChatId]
  );
  const activeMessages = messagesByChatId[activeChatId] || [];
  const isLocked = lockByChatId[activeChatId] ?? Boolean(activeChat?.isLocked);
  const isActiveGroupChat = isGroupChat(activeChat);
  const creatorId = getCreatorId(activeChat);
  const canToggleLock = Boolean(
    isActiveGroupChat &&
      String(loggedUser?._id || "") &&
      creatorId &&
      String(loggedUser?._id || "") === String(creatorId)
  );
  const activePagination = paginationByChatId[activeChatId] || { skip: PAGE_LIMIT, hasMore: true, limit: PAGE_LIMIT };
  const typingUser = typingByChatId[activeChatId];
  const roles = Array.isArray(loggedUser?.customRoles)
    ? loggedUser.customRoles.map((item) => String(item).toLowerCase())
    : [];
  const canUseGroupMode =
    userRole === "L3" ||
    (userRole === "L4" && roles.includes("project manager") && roles.includes("operations"));

  const allowedGroupIds = useMemo(
    () =>
      (Array.isArray(loggedUser?.EmpGroups) ? loggedUser.EmpGroups : [])
        .map(getRefId)
        .filter(Boolean),
    [loggedUser?.EmpGroups]
  );
  const availableGroups = useMemo(() => {
    if (!canUseGroupMode) return [];
    if (!Array.isArray(groupsState)) return [];
    if (!allowedGroupIds.length) return groupsState;
    const allowedSet = new Set(allowedGroupIds.map(normalizeComparable));
    return groupsState.filter((group) => {
      const groupId = normalizeComparable(getRefId(group));
      const groupNames = getGroupNames(group);
      return Boolean(groupId && allowedSet.has(groupId)) || groupNames.some((name) => allowedSet.has(name));
    });
  }, [canUseGroupMode, groupsState, allowedGroupIds]);

  const projectsForGroup = useMemo(() => {
    if (!selectedGroupId) return [];
    const selectedId = normalizeComparable(selectedGroupId);
    const selectedGroup = availableGroups.find((group) => normalizeComparable(getRefId(group)) === selectedId);
    const selectedGroupNames = getGroupNames(selectedGroup);
    const selectedGroupNameSet = new Set(selectedGroupNames);
    const projectList = Array.isArray(projectsState) ? projectsState : [];

    return projectList.filter((project) => {
      const projectGroupIds = getProjectGroupIds(project).map(normalizeComparable);
      if (projectGroupIds.includes(selectedId)) return true;

      const projectGroupNames = getProjectGroupNames(project);
      return projectGroupNames.some((name) => selectedGroupNameSet.has(name));
    });
  }, [projectsState, selectedGroupId, availableGroups]);

  const quotationOptions = useMemo(() => {
    const map = new Map();
    projectsForGroup.forEach((project) => {
      const optionValue = getProjectOptionValue(project);
      const projectName = getProjectName(project);
      if (!optionValue || !projectName) return;
      if (!map.has(optionValue)) {
        map.set(optionValue, {
          quotationId: optionValue,
          projectId: normalizeValue(project?._id || project?.id),
          ProjectName: projectName,
        });
      }
    });
    return Array.from(map.values());
  }, [projectsForGroup]);

  const groupModeUsers = useMemo(() => {
    if (!selectedQuotationId) return [];
    const selectedProjectKey = String(selectedQuotationId);
    const related = projectsForGroup.filter((project) => getProjectOptionValue(project) === selectedProjectKey);
    const personMap = new Map();
    related.forEach((project) => {
      (project?.Assignedperson || []).forEach((person) => {
        const id = String(person?.personid?._id || person?.personid || "").trim();
        if (!id || id === String(loggedUser?._id || "")) return;
        if (!personMap.has(id)) {
          const employee = employees.find((emp) => String(emp?._id || "") === id);
          const firstName = employee?.EmpFirstName || String(person?.name || "").split(" ")[0] || "";
          const lastName = employee?.EmpLastName || String(person?.name || "").split(" ").slice(1).join(" ") || "";
          personMap.set(id, {
            ...(employee || {}),
            _id: id,
            EmpFirstName: firstName,
            EmpLastName: lastName,
            EmpEmail: person?.email || employee?.EmpEmail || "",
          });
        }
      });
    });
    return Array.from(personMap.values());
  }, [projectsForGroup, selectedQuotationId, employees, loggedUser?._id]);

  useEffect(() => {
    chatsRef.current = chats || [];
  }, [chats]);

  useEffect(() => {
    loggedUserIdRef.current = String(loggedUser?._id || "");
  }, [loggedUser?._id]);

  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchChats({}));
    dispatch(fetchUnreadSummary());
    dispatch(fetchGroups());
    dispatch(fetchAllProjects());
  }, [dispatch]);

  useEffect(() => {
    if (!socket.connected) socket.connect();

    const onMessage = (message) => {
      const messageChatId = String(message?.chatId || message?.chat?._id || message?.roomId || "").trim();
      if (!messageChatId) return;

      const knownChats = chatsRef.current || [];
      const isKnownChat = knownChats.some((chat) => String(chat?._id || chat?.id || "") === messageChatId);
      const participantIds = Array.isArray(message?.participants)
        ? message.participants.map((item) => String(item?._id || item?.id || item || ""))
        : Array.isArray(message?.members)
          ? message.members.map((item) => String(item?._id || item?.id || item || ""))
          : [];
      const currentUserId = loggedUserIdRef.current;
      const isParticipantMatch =
        currentUserId && participantIds.length > 0 && participantIds.includes(currentUserId);

      // Ignore messages that are not for this user's known chat list or participant scope.
      if (!isKnownChat && !isParticipantMatch) return;
      dispatch(addMessage(message));
    };
    const onLocked = (payload = {}) => {
      dispatch(setChatLock({ chatId: payload.chatId, isLocked: true }));
    };
    const onUnlocked = (payload = {}) => {
      dispatch(setChatLock({ chatId: payload.chatId, isLocked: false }));
    };
    const onTyping = (payload = {}) => {
      dispatch(setTyping({ chatId: payload.chatId, user: payload.user || payload.sender || payload.senderName || "User" }));
      window.clearTimeout(typingClearRef.current);
      typingClearRef.current = window.setTimeout(() => {
        dispatch(clearTyping(payload.chatId));
      }, 1600);
    };
    const onConnect = () => {
      if (activeChatId) {
        socket.emit("join_chat", { chatId: activeChatId });
      }
      socket.emit("unread_sync");
    };
    const onConnectError = (error) => {
      dispatch(setChatError(error?.message || "Socket connection error"));
    };
    const onUnreadSync = (payload = {}) => {
      dispatch(applyUnreadSummary(payload));
    };
    const onReadAck = () => {
      socket.emit("unread_sync");
    };

    socket.on("new_message", onMessage);
    socket.on("chat_locked", onLocked);
    socket.on("chat_unlocked", onUnlocked);
    socket.on("user_typing", onTyping);
    socket.on("connect", onConnect);
    socket.on("connect_error", onConnectError);
    socket.on("chat:unread_sync", onUnreadSync);
    socket.on("chat:read_ack", onReadAck);

    return () => {
      socket.off("new_message", onMessage);
      socket.off("chat_locked", onLocked);
      socket.off("chat_unlocked", onUnlocked);
      socket.off("user_typing", onTyping);
      socket.off("connect", onConnect);
      socket.off("connect_error", onConnectError);
      socket.off("chat:unread_sync", onUnreadSync);
      socket.off("chat:read_ack", onReadAck);
      window.clearTimeout(typingClearRef.current);
      window.clearTimeout(typingDebounceRef.current);
    };
  }, [dispatch, activeChatId]);

  useEffect(() => {
    if (!activeChatId) return;
    socket.emit("join_chat", { chatId: activeChatId });
    dispatch(fetchMessages({ chatId: activeChatId, skip: 0, limit: PAGE_LIMIT }));
    dispatch(markChatAsRead(activeChatId));
    socket.emit("mark_read", { chatId: activeChatId });
    socket.emit("unread_sync");
  }, [dispatch, activeChatId]);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [activeChatId, activeMessages.length]);

  const openChat = (chat) => {
    const chatId = getChatId(chat);
    if (!chatId) return;
    dispatch(setActiveChat(chatId));
    const participants = Array.isArray(chat?.participants)
      ? chat.participants.map((item) => String(item?._id || item?.id || item)).filter(Boolean)
      : [];
    dispatch(setParticipantIDs(participants));
  };

  const onCreateDirectChat = (user) => {
    createChatRequest([String(user?._id)].filter(Boolean), "direct", user);
  };

  const onToggleCreateUser = (user) => {
    const id = String(user?._id || "");
    if (!id) return;
    setSelectedCreateUsers((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const createChatRequest = async (participantIds = [], explicitType = null, directUser = null, extraPayload = {}) => {
    const creatorId = String(loggedUser?._id || "");
    const cleanParticipantIds = Array.from(new Set([creatorId, ...participantIds].filter(Boolean)));
    if (cleanParticipantIds.length < 2) {
      enqueueSnackbar("At least two participants are required to create a chat.", { variant: "warning" });
      return;
    }
    const selectedCountExcludingSelf = cleanParticipantIds.filter((id) => id !== creatorId).length;
    const computedType = explicitType || (selectedCountExcludingSelf <= 1 ? "direct" : "group");
    const selectedUsers = employees.filter((emp) => cleanParticipantIds.includes(String(emp?._id || "")));
    const memberNames = selectedUsers.map((emp) => getFullName(emp)).filter(Boolean);
    const computedName =
      computedType === "direct" && selectedUsers.length > 0
        ? buildChatNameFromMembers(memberNames.slice(0, 2))
        : buildChatNameFromMembers(memberNames);

    const payload = {
      name: computedName,
      type: computedType,
      members: cleanParticipantIds,
      // Keep old field for backward compatibility with any existing backend parser.
      participantIds: cleanParticipantIds,
      ...extraPayload,
      ...(computedType === "direct" && directUser ? { directUserId: String(directUser?._id || "") } : {}),
    };
    try {
      const createdChat = await dispatch(createChat(payload)).unwrap();
      const createdChatEntity = createdChat?.chat || createdChat?.data || createdChat;
      const chatId = getChatId(createdChatEntity);
      if (!chatId) {
        enqueueSnackbar("Chat created, but response is missing chat id.", { variant: "warning" });
        dispatch(fetchChats({}));
        return;
      }
      dispatch(fetchChats({}));
      openChat(createdChatEntity);
      enqueueSnackbar("Chat created successfully.", { variant: "success" });
      setSelectedCreateUsers([]);
    } catch (error) {
      enqueueSnackbar(
        error?.message || error?.error || "Unable to create chat. Please confirm POST /api/chats contract.",
        { variant: "error" }
      );
    }
  };

  const onCreateGroupChat = async () => {
    const selectedUsersSnapshot = [...selectedCreateUsers];
    setSelectedCreateUsers([]);
    setUserSearch("");
    if (chatMode === "group") {
      if (!selectedGroupId) {
        enqueueSnackbar("Please select group.", { variant: "warning" });
        return;
      }
      if (!selectedQuotationId) {
        enqueueSnackbar("Please select project.", { variant: "warning" });
        return;
      }
      await createChatRequest(selectedUsersSnapshot, "group", null, {
        groupId: selectedGroupId,
        quotationId: selectedQuotationId,
      });
      return;
    }
    await createChatRequest(selectedUsersSnapshot);
  };

  const handleDeleteMessage = async (message) => {
    const messageId = String(message?._id || "");
    if (!messageId) return;
    const confirmed = window.confirm("Delete this message?");
    if (!confirmed) return;
    try {
      await dispatch(deleteChatMessage(messageId)).unwrap();
      enqueueSnackbar("Message deleted.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        error?.message || error?.error || "Failed to delete message. Please confirm DELETE /api/messages/:messageId",
        { variant: "error" }
      );
    }
  };

  const handleDeleteGroup = async () => {
    const chatId = getChatId(activeChat);
    if (!chatId) return;
    const confirmed = window.confirm("Delete this group chat for all participants?");
    if (!confirmed) return;
    try {
      await dispatch(deleteChat(chatId)).unwrap();
      enqueueSnackbar("Group chat deleted.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        error?.message || error?.error || "Failed to delete group. Please confirm DELETE /api/chats/:chatId",
        { variant: "error" }
      );
    }
  };

  const handleLoadMore = () => {
    if (!activeChatId || loadingMessages || !activePagination?.hasMore) return;
    dispatch(fetchMessages({ chatId: activeChatId, skip: activePagination.skip || 0, limit: activePagination.limit || PAGE_LIMIT }));
  };

  const emitTyping = () => {
    if (!activeChatId || !socket.connected) return;
    socket.emit("typing", { chatId: activeChatId });
  };

  const handleInputChange = (value) => {
    setInput(value);
    window.clearTimeout(typingDebounceRef.current);
    typingDebounceRef.current = window.setTimeout(() => emitTyping(), 250);
  };

  const handleFileSelection = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
  };

  const sendTextMessage = async (text) => {
    if (!activeChatId || !text.trim()) return;
    const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const senderId = loggedUser?._id || localStorage.getItem("userId");
    dispatch(addPendingMessage({ chatId: activeChatId, tempId, messageText: text, type: "text", senderId }));
    try {
      await dispatch(postMessage({ chatId: activeChatId, messageText: text.trim(), type: "text" })).unwrap();
    } catch (error) {
      dispatch(markPendingAsFailed({ chatId: activeChatId, tempId }));
      enqueueSnackbar(error?.message || "Failed to send text message", { variant: "error" });
    }
  };

  const sendFileMessages = async () => {
    if (!activeChatId || selectedFiles.length === 0) return;
    for (const file of selectedFiles) {
      const tempId = `tmp_file_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const senderId = loggedUser?._id || localStorage.getItem("userId");
      try {
        const uploaded = await dispatch(uploadChatFile(file)).unwrap();
        const attachment = normalizeUploadedAttachment(uploaded, file);
        if (!attachment.fileId && !attachment.url) {
          enqueueSnackbar(`Upload response missing file details for ${attachment.name || file?.name}`, { variant: "warning" });
          continue;
        }
        dispatch(
          addPendingMessage({
            chatId: activeChatId,
            tempId,
            messageText: attachment.name || "Attachment",
            type: "file",
            senderId,
            attachments: [attachment],
          })
        );

        const fileMessagePayload = {
          chatId: activeChatId,
          messageText: attachment.name || "Attachment",
          type: "file",
          fileId: attachment.fileId || undefined,
          attachmentId: attachment.attachmentId || undefined,
          fileName: attachment.fileName,
          fileUrl: attachment.fileUrl,
          attachments: [attachment],
          files: [attachment],
        };

        await dispatch(
          postMessage(fileMessagePayload)
        ).unwrap();
      } catch (error) {
        dispatch(markPendingAsFailed({ chatId: activeChatId, tempId }));
        enqueueSnackbar(error?.message || `Failed to send file: ${file.name}`, { variant: "error" });
      }
    }
    setSelectedFiles([]);
  };

  const handleSendMessage = async () => {
    if (!activeChatId || isLocked) return;
    const text = String(input || "");
    setInput("");
    await sendFileMessages();
    await sendTextMessage(text);
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const toggleLock = async () => {
    if (!activeChatId) return;
    if (!canToggleLock) {
      if (!isActiveGroupChat) {
        enqueueSnackbar("Lock is disabled for 1:1 chat.", { variant: "info" });
      } else {
        enqueueSnackbar("Only the group creator can lock/unlock this chat.", { variant: "warning" });
      }
      return;
    }
    try {
      if (isLocked) {
        await dispatch(unlockChat(activeChatId)).unwrap();
      } else {
        await dispatch(lockChat(activeChatId)).unwrap();
      }
    } catch (error) {
      enqueueSnackbar(error?.message || "Failed to update chat lock", { variant: "error" });
    }
  };

  return (
    <div className="flex flex-col bg-white h-[min(82vh,40rem)] w-full max-w-full overflow-hidden rounded-lg">
      <div className="h-[9%] w-full flex justify-center items-center text-center text-white font-semibold bg-[#1f41bb] rounded-t-md">
        Communication
      </div>
      <div className="h-full flex min-h-0">
        <ChatLeftPanel
          chats={chats}
          activeChatId={activeChatId}
          onOpenChat={openChat}
          users={chatMode === "group" ? groupModeUsers : employees}
          userSearch={userSearch}
          setUserSearch={setUserSearch}
          onCreateDirectChat={onCreateDirectChat}
          selectedCreateUserIds={selectedCreateUsers}
          onToggleCreateUser={onToggleCreateUser}
          onCreateSelectedChat={onCreateGroupChat}
          creatingChat={creatingChat}
          loggedUserId={loggedUser?._id}
          chatMode={chatMode}
          canUseGroupMode={canUseGroupMode}
          onChangeChatMode={(nextMode) => {
            setChatMode(nextMode);
            setSelectedCreateUsers([]);
            setUserSearch("");
          }}
          availableGroups={availableGroups}
          selectedGroupId={selectedGroupId}
          onSelectGroup={(value) => {
            setSelectedGroupId(value);
            setSelectedQuotationId("");
            setSelectedCreateUsers([]);
          }}
          quotationOptions={quotationOptions}
          selectedQuotationId={selectedQuotationId}
          onSelectQuotation={(value) => {
            setSelectedQuotationId(value);
            setSelectedCreateUsers([]);
          }}
        />
        <div className="w-[55%] flex flex-col min-h-0">
          <ChatTopBar
            chatName={activeChat ? resolveChatDisplayName(activeChat, loggedUser?._id, employees) : "No chat selected"}
            isLocked={isLocked}
            onToggleLock={toggleLock}
            canToggleLock={canToggleLock}
            showDeleteGroup={isActiveGroupChat}
            onDeleteGroup={handleDeleteGroup}
          />
          <ChatWindow
            messages={activeMessages}
            hasMore={Boolean(activePagination?.hasMore)}
            isLoadingMore={loadingMessages}
            onLoadMore={handleLoadMore}
            loggedUserId={loggedUser?._id}
            isLocked={isLocked}
            input={input}
            setInput={handleInputChange}
            onSendMessage={handleSendMessage}
            onInputKeyDown={handleInputKeyDown}
            onFileSelect={handleFileSelection}
            selectedFiles={selectedFiles}
            messagesRef={messagesRef}
            typingUserLabel={typingUser}
            canChat={Boolean(activeChatId)}
            onDeleteMessage={handleDeleteMessage}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatFilters;
